import { OrderFeeService } from '@aca-new/app/pages/payments/pages/payments-content/shared/services/order-fee/order-fee.service';
import { ReflowService } from '@aca-new/app/pages/reflow/shared/services/reflow.service';
import { ClipboardComponent } from '@aca-new/app/shared/components/clipboard/clipboard.component';
import { CurrencyViewModule } from '@aca-new/app/shared/components/currency-view/currency-view.module';
import { DialogHeaderModule } from '@aca-new/app/shared/components/modal/shared/components/dialog-header/dialog-header.module';
import { IMAGES } from '@aca-new/app/shared/constants/images.constants';
import { LoadingDirective } from '@aca-new/app/shared/directives/loading/loading.directive';
import { TrackByUuidDirective } from '@aca-new/app/shared/directives/track-by-uuid.directive';
import { IBankInfo } from '@aca-new/app/shared/models/interfaces/bank-info.interface';
import { QimaButtonService } from '@aca-new/app/shared/services/exported-services/qima-button/qima-button.service';
import { QimaButtonIconService } from '@aca-new/app/shared/services/exported-services/qima-button-icon/qima-button-icon.service';
import { QimaIconService } from '@aca-new/app/shared/services/exported-services/qima-icon/qima-icon.service';
import { QimaTooltipService } from '@aca-new/app/shared/services/exported-services/qima-tooltip/qima-tooltip.service';
import { AppFileDownloadService } from '@aca-new/app/shared/services/file-services/app-file-download.service';
import { AppSnackbarService } from '@aca-new/app/shared/services/modal-services/app-snackbar/app-snackbar.service';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  IQimaFile,
  EQimaMimeType,
  QimaFileModule,
  QimaIconModule,
  QimaButtonModule,
  EQimaSnackbarType,
  QimaInputFileSelectModule,
  QimaTooltipDirectiveModule,
} from '@qima/ngx-qima';

interface IBankDisplayInfo {
  canCopy: boolean;
  key: string;
  value: string;
}

@UntilDestroy()
@Component({
  selector: 'app-pay-by-transfer',
  templateUrl: './pay-by-transfer.component.html',
  styleUrls: ['./pay-by-transfer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ReflowService, OrderFeeService, QimaButtonIconService],
  imports: [
    CommonModule,
    QimaFileModule,
    QimaIconModule,
    TranslateModule,
    QimaButtonModule,
    LoadingDirective,
    ClipboardComponent,
    CurrencyViewModule,
    DialogHeaderModule,
    TrackByUuidDirective,
    QimaInputFileSelectModule,
    QimaTooltipDirectiveModule,
  ],
})
export class PayByTransferComponent {
  /**
   * @description
   * Bank info
   * @type {IBankInfo | undefined}
   * @default undefined
   */
  @Input('payByTransferBankInfo')
  public bankInfo: IBankInfo | undefined = undefined;

  /**
   * @description
   * Whether the component is loading
   * @type {boolean}
   * @default false
   */
  @Input('payByTransferIsLoading')
  public isLoading: boolean = false;

  @Output('payByTransferIsLoadingChange')
  public readonly isLoadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output('payByTransferPaymentSubmit')
  public readonly paymentSubmit: EventEmitter<IQimaFile[]> = new EventEmitter<IQimaFile[]>();

  public readonly lockIcon: string = IMAGES.creditCardLeft;
  public uploadFiles: IQimaFile[] = [];
  public fileSelectAccept = [EQimaMimeType.IMAGE_PNG, EQimaMimeType.IMAGE_JPG, EQimaMimeType.IMAGE_JPEG, EQimaMimeType.APPLICATION_PDF];
  public readonly qimaMimeType = EQimaMimeType;

  public constructor(
    public readonly qimaIcon: QimaIconService,
    public readonly qimaButton: QimaButtonService,
    public readonly qimaTooltip: QimaTooltipService,
    public readonly qimaButtonIcon: QimaButtonIconService,
    private readonly _translateService: TranslateService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _appSnackbarService: AppSnackbarService,
    private readonly _appFileDownloadService: AppFileDownloadService
  ) {}

  public get bankInfoList(): IBankDisplayInfo[] {
    if (!this.bankInfo) {
      return [];
    }

    return [
      { canCopy: false, key: 'PAY_BY_TRANSFER.BANK_NAME', value: this.bankInfo.bankName },
      { canCopy: false, key: 'PAY_BY_TRANSFER.ACCOUNT_NAME', value: this.bankInfo.accountName },
      { canCopy: true, key: 'PAY_BY_TRANSFER.ACCOUNT_NUMBER', value: this.bankInfo.accountNumber },
      { canCopy: false, key: 'PAY_BY_TRANSFER.BANK_ADDRESS', value: this.bankInfo.bankAddress },
      { canCopy: true, key: 'PAY_BY_TRANSFER.SWIFT', value: this.bankInfo.swiftCode },
    ];
  }

  public onPdfDownloadClick(): void {
    if (this.bankInfo) {
      this._appFileDownloadService.downloadFileDirectlyByFileId(this.bankInfo.fileName, this.bankInfo.fileId);
    }
  }

  public onFileSelectFilesChange(files: IQimaFile[]): void {
    const totalSize = this.uploadFiles.concat(files).reduce((total, file): number => {
      return total + file.size;
    }, 0);

    if (totalSize > 40 * 1024 * 1024) {
      this._appSnackbarService.showSnackbar(this._translateService.instant('PAY_BY_TRANSFER.UPLOAD_FILES_OVER_LIMIT') as string, EQimaSnackbarType.WARNING, 2000);

      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (!this.fileSelectAccept.includes(files[i].type as EQimaMimeType)) {
        this._appSnackbarService.showSnackbar(
          this._translateService.instant('FILE_UPLOAD.FILE_REJECTED', { name: files[i].name ?? '' }) as string,
          EQimaSnackbarType.WARNING,
          2000
        );

        return;
      }
    }

    if (this.uploadFiles.length > 0) {
      this.uploadFiles = this.uploadFiles.concat(files);
    } else {
      this.uploadFiles = files;
    }

    this._changeDetectorRef.markForCheck();
  }

  public onRemoveClick(_file: IQimaFile, idx: number): void {
    this.uploadFiles.splice(idx, 1);
    this._changeDetectorRef.markForCheck();
  }

  public onSubmit(): void {
    this.paymentSubmit.emit(this.uploadFiles);
  }
}
